import {
  makeSelectedEvent,
  selectSelectedItems,
} from 'app/pages/TicketSalePage/slice/selectors';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { isTicket } from 'types/SelectedItem';
import { TicketTypeBundleItem } from 'types/TicketsState';
import {
  getSelectedTicketQuantityFromAllBundles,
  getTicketTypes,
} from 'utils/bundles';
import {
  EVENT_TYPE_PAID,
  EVENT_TYPE_WITH_CALENDAR,
} from 'utils/static/constants';
import { getTicketLimitOnCalendar } from '../TicketSection/utils';
import { dateFormatForCalendar } from 'utils/calendar';

interface useTicketTypeBundleIsSoldOutProps {
  items: TicketTypeBundleItem[];
  selectedDate: DateTime | undefined;
}

const useTicketTypeBundleIsSoldOut = ({
  items,
  selectedDate,
}: useTicketTypeBundleIsSoldOutProps) => {
  const { type, tickets, ticketTypeBundles, calendar, reservedTickets } =
    useSelector(makeSelectedEvent);
  const selectedItems = useSelector(selectSelectedItems);

  const ticketTypes = getTicketTypes(items, tickets);

  if (type === EVENT_TYPE_WITH_CALENDAR) {
    const defaultSoldOut = ticketTypes.some(ticketType => {
      const limit = getTicketLimitOnCalendar(
        calendar!,
        reservedTickets!,
        ticketType.id,
        selectedDate!,
      );

      return limit === 0;
    });

    if (defaultSoldOut) return true;

    const ticketTypesWithLimitedQuantity = ticketTypes.filter(ticketType => {
      const limit = getTicketLimitOnCalendar(
        calendar!,
        reservedTickets!,
        ticketType.id,
        selectedDate!,
      );

      return limit !== null;
    });

    if (ticketTypesWithLimitedQuantity.length === 0) return false;

    for (const ticketType of ticketTypesWithLimitedQuantity) {
      const requiredQuantityToBuyBundle = (
        items.find(
          ({ idTicketType }) => idTicketType === ticketType.id,
        ) as TicketTypeBundleItem
      ).quantity;

      const ticketSelectedQuantity =
        selectedItems.find(
          selectedItem =>
            isTicket(selectedItem) &&
            selectedItem.ticketId === ticketType.id &&
            selectedItem.date === selectedDate!.toFormat(dateFormatForCalendar),
        )?.quantity ?? 0;

      const ticketSelectedQuantityFromBundles =
        getSelectedTicketQuantityFromAllBundles(
          ticketTypeBundles!,
          selectedItems,
          ticketType.id,
          selectedDate!,
        );

      const totalSelectedQuantity =
        ticketSelectedQuantity + ticketSelectedQuantityFromBundles;

      const limit = getTicketLimitOnCalendar(
        calendar!,
        reservedTickets!,
        ticketType.id,
        selectedDate!,
      );

      if (limit! - totalSelectedQuantity < requiredQuantityToBuyBundle)
        return true;
    }
  } else if (type === EVENT_TYPE_PAID) {
    const defaultSoldOut = ticketTypes.some(({ limit }) => limit === 0);

    if (defaultSoldOut) return true;

    // if the bundle has only tickets with unlimited quantity, it cannot be sold out
    const ticketTypesWithLimitedQuantity = ticketTypes.filter(
      ({ limit }) => limit !== null,
    );

    if (ticketTypesWithLimitedQuantity.length === 0) return false;

    // if the bundle has tickets with limited quantity, we need to search them in selectedItems.
    // case 1: when one of them has limit reached => return true
    // case 2: a ticket limit can be reached not only by adding that ticket inside selectedItems,
    // but also by adding one or mode bundles inside selectedItems ( ofc, if the bundle includes the ticket ).
    // in this case,the bundle is sold out if any of the selectedTickets has remaining quantity
    // lower than the required quantity for buying the bundle

    for (const ticketType of ticketTypesWithLimitedQuantity) {
      const requiredQuantityToBuyBundle = (
        items.find(
          ({ idTicketType }) => idTicketType === ticketType.id,
        ) as TicketTypeBundleItem
      ).quantity;

      const ticketSelectedQuantity =
        selectedItems.find(
          selectedItem =>
            isTicket(selectedItem) && selectedItem.ticketId === ticketType.id,
        )?.quantity ?? 0;

      const ticketSelectedQuantityFromBundles =
        getSelectedTicketQuantityFromAllBundles(
          ticketTypeBundles!,
          selectedItems,
          ticketType.id,
        );

      const totalSelectedQuantity =
        ticketSelectedQuantity + ticketSelectedQuantityFromBundles;

      if (
        ticketType.limit! - totalSelectedQuantity <
        requiredQuantityToBuyBundle
      )
        return true;
    }
  }

  return false;
};

export default useTicketTypeBundleIsSoldOut;
